import { detectPlatform } from "@/Utils/version";

// Versión y plataforma del shell nativo, mandadas al backend como
// `X-Eriol-Version` / `X-Eriol-Platform` en cada request del renderer dentro
// de Electron, junto con `X-Eriol-Observed-At` (cuándo se armó la request). El
// backend las guarda por usuario (users.desktopVersion) para que el backoffice
// muestre qué versión tiene instalada cada cuenta, y ordena las escrituras por
// la marca: una request vieja que llega tarde no pisa lo que reportó una
// versión más nueva.
//
// Las lee UN solo constructor de headers autenticados: `buildAuthHeaders`
// (lib/authFetch.ts). services/api.ts, authFetch y los servicios con fetch
// propio (listado e integraciones de donaciones, ticket de señalización)
// pasan por ahí; un cliente del backend que arme su Authorization a mano se
// saltea el seguimiento y la cuenta nunca actualiza desktopVersionSeenAt.
//
// Una sola excepción, a sabiendas: el EventSource de /donations/stream
// (services/DonationsService.ts). EventSource no admite headers propios y se
// autentica con `?accessKey=` en la query, así que esa conexión no reporta la
// versión. No hace falta que la reporte: el backend la guarda por usuario y
// no por conexión (y la reescribe a lo sumo cada 15 min), y el stream solo se
// abre con la sesión iniciada en la app, donde el renderer ya manda requests
// autenticadas que sí la llevan: /auth/me al arrancar (useAuthBootstrap) y
// cada minuto (useSessionValidator, fijado en backendClientHeaders.test.ts).
// Hacer que cuente pedía leer la versión de la query en AccessKeyGuard, que
// autentica todo el backend, o cambiar el EventSource por un fetch con stream
// y reimplementar la reconexión de las alertas de donación: más riesgo que
// dato, para una versión que ya llega por otro lado.
//
// Sin importar api.ts ni authFetch (evita ciclos): ellos llaman a este módulo.

/**
 * Cuánto espera una request a que el IPC conteste la versión antes de salir
 * sin ella. `getAppVersion` contesta en milisegundos; el tope existe para que
 * un main trabado no cuelgue ninguna request, y cuenta desde que se hizo el
 * pedido, no desde cada request: pasado el tope, nadie más espera ese pedido.
 *
 * Solo hace esperar el PRIMER pedido, que es el que tiene que llegar a las
 * requests del arranque. Los reintentos no: con un main colgado, cada
 * reintento volvía a frenar 1,5 s las requests cada DESKTOP_VERSION_RETRY_MS.
 */
export const DESKTOP_VERSION_WAIT_MS = 1_500;

/**
 * Cuánto después de un pedido que NO trajo versión se vuelve a pedir: contestó
 * `null` o "Unknown", rechazó, tiró, o no contestó nunca.
 *
 * Los cuatro casos van juntos porque desde acá no se distinguen. El preload
 * real (extension SystemPreload.getAppVersion) atrapa cualquier error del IPC
 * y devuelve `null`, y el handler del main hace lo mismo: un fallo transitorio
 * llega como un `null` que parece definitivo, nunca como un rechazo. Tomar el
 * `null` como respuesta final dejaba al proceso sin reportar su versión hasta
 * que se cerrara, y un IPC colgado dejaba el pedido "en curso" para siempre.
 * Con un intervalo y no en cada request: si el main de verdad no puede dar la
 * versión, son dos llamadas por minuto y no una por request.
 */
export const DESKTOP_VERSION_RETRY_MS = 30_000;

let shellVersion: string | null = null;

/** Pedido en curso, compartido por todas las requests que salen mientras tanto. */
let pending: Promise<void> | null = null;

/** Hasta cuándo esperan las requests el pedido en curso (epoch ms). */
let waitUntil = 0;

/** Antes de esto no se hace otro pedido (epoch ms). */
let retryAt = 0;

type ShellVersionBridge = {
  electronAPI?: { getAppVersion?: () => Promise<string | null> };
};

/**
 * Pide la versión al main por IPC (`electronAPI.getAppVersion`) y devuelve el
 * pedido en curso, o `null` si no hay nada que esperar: fuera de Electron, sin
 * `getAppVersion` en el preload, con la versión ya conocida, o porque el
 * último pedido terminó sin versión y todavía no pasó
 * DESKTOP_VERSION_RETRY_MS.
 *
 * Una versión recibida es definitiva (el proceso no cambia de versión); todo
 * lo demás se vuelve a pedir pasado el intervalo, incluido un pedido que
 * sigue sin contestar. Si ese pedido viejo contesta más tarde, su versión
 * cuenta igual.
 */
export function primeDesktopClientHeaders(): Promise<void> | null {
  if (shellVersion) return null;
  if (typeof window === "undefined") return null;

  const now = Date.now();
  if (now < retryAt) return pending;

  const getAppVersion = (window as unknown as ShellVersionBridge).electronAPI
    ?.getAppVersion;
  if (typeof getAppVersion !== "function") return null;

  let request: Promise<string | null>;
  try {
    request = Promise.resolve(getAppVersion());
  } catch (err) {
    request = Promise.reject(err);
  }

  const firstAttempt = retryAt === 0;
  if (firstAttempt) waitUntil = now + DESKTOP_VERSION_WAIT_MS;
  retryAt = now + DESKTOP_VERSION_RETRY_MS;

  const attempt: Promise<void> = request
    .then(
      (version) => {
        // Preloads/handlers viejos devuelven el sentinel "Unknown" ante error:
        // no es una versión (misma normalización que services/desktopUpdater.ts).
        if (version && version !== "Unknown") shellVersion = version;
      },
      () => {
        // Sin versión por ahora: se vuelve a pedir pasado el intervalo.
      },
    )
    .then(() => {
      if (pending === attempt) pending = null;
    });

  pending = attempt;

  return attempt;
}

function headersFor(version: string): Record<string, string> {
  return {
    "X-Eriol-Version": version,
    "X-Eriol-Platform": detectPlatform(),
    "X-Eriol-Observed-At": String(Date.now()),
  };
}

/**
 * Los headers de escritorio de UNA request autenticada. Si la versión todavía
 * no llegó por IPC, espera el pedido en curso hasta DESKTOP_VERSION_WAIT_MS
 * contados desde que se hizo: las primeras requests del arranque (la primera
 * observación de la cuenta) salen con la versión, y un IPC que no contesta no
 * cuelga ninguna.
 *
 * `{}` fuera de Electron o si la versión no llegó a tiempo. Se ceba sola:
 * ningún camino depende de que otro módulo haya pedido la versión antes.
 */
export async function getDesktopClientHeaders(): Promise<Record<string, string>> {
  const attempt = primeDesktopClientHeaders();

  if (attempt && !shellVersion) {
    const remaining = waitUntil - Date.now();

    if (remaining > 0) {
      let timer: ReturnType<typeof setTimeout> | undefined;

      await Promise.race([
        attempt,
        new Promise<void>((resolve) => {
          timer = setTimeout(resolve, remaining);
        }),
      ]);

      clearTimeout(timer);
    }
  }

  return shellVersion ? headersFor(shellVersion) : {};
}

/**
 * Los headers de escritorio SIN esperar al IPC: los de la versión si ya
 * llegó, `{}` si no (y el pedido queda hecho para las que vienen).
 *
 * Para las requests sin autenticar. El backend anota la versión solo en las
 * que autentican (AccessKeyGuard → AuthService.recordDesktopClient), así que
 * hacerlas esperar hasta DESKTOP_VERSION_WAIT_MS en el arranque —justo cuando
 * el main tarda— era demora sin ningún dato a cambio.
 */
export function peekDesktopClientHeaders(): Record<string, string> {
  void primeDesktopClientHeaders();

  return shellVersion ? headersFor(shellVersion) : {};
}

/** Solo tests. */
export function __resetDesktopClientHeadersForTests(): void {
  shellVersion = null;
  pending = null;
  waitUntil = 0;
  retryAt = 0;
}
